import { ReactNode, useState } from 'react';
import { IconDots } from '@rossum/ui/icons/tabler';
import {
  Box,
  IconButton,
  Menu,
  Stack,
  SvgIcon,
  SxProps,
  Theme,
  useTheme,
} from '@rossum/ui/material';
import {
  OverflowContainerItem,
  useOverflowContainer,
} from './useOverflowContainer';

type OverflowContainerProps = {
  items: ReadonlyArray<OverflowContainerItem>;
  budget?: number;
  sx?: SxProps<Theme>;
  gap?: number;
  renderTrigger?: (props: {
    onClick: (e: React.MouseEvent<HTMLElement>) => void;
    overflowCount: number;
  }) => ReactNode;
};

// Approximate px width of the default IconButton trigger.
// TODO: Measure the actual rendered trigger width for accuracy.
const DEFAULT_TRIGGER_WIDTH_PX = 26;

const OverflowContainer = ({
  items,
  budget,
  sx,
  gap = 1,
  renderTrigger,
}: OverflowContainerProps) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const gapPx = parseInt(theme.spacing(gap), 10);

  const { rootRef, itemRef, visibleItems, overflowItems, ready } =
    useOverflowContainer({
      items,
      budget,
      triggerWidth: DEFAULT_TRIGGER_WIDTH_PX,
      gap: gapPx,
    });

  const hasOverflow = overflowItems.length > 0;

  const handleTriggerClick = (e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <Box
      ref={rootRef}
      sx={{
        minWidth: 0,
        ...sx,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        useFlexGap
        spacing={gap}
        sx={{
          minWidth: 0,
          visibility: ready ? 'visible' : 'hidden',
        }}
      >
        {visibleItems.map(item => (
          <div key={item.id} style={{ flexShrink: 0 }}>
            {item.renderInline()}
          </div>
        ))}

        {hasOverflow ? (
          <>
            {renderTrigger ? (
              renderTrigger({
                onClick: handleTriggerClick,
                overflowCount: overflowItems.length,
              })
            ) : (
              <IconButton
                onClick={handleTriggerClick}
                size="small"
                sx={{ flexShrink: 0 }}
                data-cy="overflow-more-button"
              >
                <SvgIcon fontSize="small">
                  <IconDots />
                </SvgIcon>
              </IconButton>
            )}
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              {overflowItems.map(item => (
                <div key={item.id}>
                  {item.renderOverflow({ close: handleMenuClose })}
                </div>
              ))}
            </Menu>
          </>
        ) : null}
      </Stack>

      {/* Hidden sandbox: mirrors the visible layout so items can be measured before partitioning. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          height: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        <Stack direction="row" alignItems="center" useFlexGap spacing={gap}>
          {items.map(item => (
            <div key={item.id} ref={itemRef(item.id)} style={{ flexShrink: 0 }}>
              {item.renderInline({ measurement: true })}
            </div>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export { OverflowContainer };
export type { OverflowContainerProps };
